import type { ArtifactPreview } from "~/services/artifacts";
import { AttachmentCard } from "./AttachmentCard";
import { useConversationContext } from "./ConversationContext";
import { useArtifactPreviewById } from "./artifacts/useArtifactPreview";

function imageUrl(content: ArtifactPreview["content"]): string | undefined {
  if (!content || typeof content !== "object" || Array.isArray(content)) return undefined;
  const url = (content as Record<string, unknown>).url;
  return typeof url === "string" ? url : undefined;
}

// Renders one attachment on a sent user bubble as the same compact AttachmentCard
// the composer uses — resolved by artifact id via the preview endpoint, with no
// remove, maximize, or download affordances. Serves both the live turn and reload.
export const UserAttachment = ({ id }: { id: string }) => {
  const { conversationId, shareToken } = useConversationContext();
  const { preview, isLoading } = useArtifactPreviewById({ conversationId, shareToken }, id);

  if (isLoading) {
    return (
      <div className="h-16 w-52 animate-pulse rounded-lg bg-muted/60" aria-label="Attachment" />
    );
  }

  // Unresolved (deleted / preview error): still show a neutral file card so the
  // attachment doesn't silently vanish.
  if (!preview) {
    return <AttachmentCard filename="Attachment" />;
  }

  const filename = preview.filename ?? preview.artifact_name ?? "Attachment";
  const src = preview.artifact_type === "image" ? imageUrl(preview.content) : undefined;
  return <AttachmentCard filename={filename} imageUrl={src} />;
};
